// pages/CallDetailsPage.tsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Phone, ArrowLeft, Clock, User, Tag, Calendar, FileText, PhoneIncoming, PhoneOutgoing, Loader2, Volume2, History } from 'lucide-react';
import { Call } from '../../../types/features/Call';
import { useSoftphoneContext } from '../../../contexts/SoftphoneContext';
import { callService } from '../../../services/callService';
import { alertError } from '../../../helpers/alertError';
import { formatPhoneNumber } from '../../../utils/formatPhonenumber';
import { AudioPlayer } from '../../../components/ui/AudioPlayer';

export default function CallDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [call, setCall] = useState<Call | null>(null);
  const [loading, setLoading] = useState(true);
  
  const [audioSrc, setAudioSrc] = useState<string | null>(null);
  const [isLoadingAudio, setIsLoadingAudio] = useState<boolean>(false);
  const [showPlayer, setShowPlayer] = useState<boolean>(false);

  const [clientCalls, setClientCalls] = useState<Call[]>([]);
  const [loadingClientCalls, setLoadingClientCalls] = useState<boolean>(false);

  const { handleMakeCall } = useSoftphoneContext();

  useEffect(() => {
    return () => {
      if (audioSrc) {
        URL.revokeObjectURL(audioSrc);
      }
    };
  }, [audioSrc]);

  useEffect(() => {
    const fetchCallDetails = async () => {
      try {
        setLoading(true);
        const callData = await callService.getCallById(`${id}`);
        setCall(callData);

        const phoneNumber = callData?.client_phone || callData?.client?.phone || callData?.phone;
        if (phoneNumber) {
          fetchClientCalls(phoneNumber, callData?.id!);
        }
      } catch (error: any) {
        console.error('Erreur lors du chargement des détails de l\'appel:', error.response);
        alertError('Impossible de charger les détails de l\'appel');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCallDetails();
    }
  }, [id]);

  const fetchClientCalls = async (phone: string, currentCallId: string | number) => {
    try {
      setLoadingClientCalls(true);
      const response = await callService.getCallsByClient(call?.client_id || call?.client?.id || "");
      const callsList = response.data  || response || [];
      const filtered = callsList.filter((c: Call) => {
        const cPhone = c.client_phone || c.client?.phone || c.phone;
        return cPhone === phone && String(c.id) !== String(currentCallId);
      });
      setClientCalls(filtered);
    } catch (error: any) {
      console.error("Erreur lors du chargement de l'historique du client", error);
    } finally {
      setLoadingClientCalls(false);
    }
  };

  const handleLoadAudio = async () => {
    if (!call) return;

    if (audioSrc) {
      setShowPlayer(!showPlayer);
      return;
    }

    try {
      setIsLoadingAudio(true);
      const blobUrl = await callService.getCallAudioStream(call.id);
      setAudioSrc(blobUrl);
      setShowPlayer(true);
    } catch (error: any) {
      console.error("Erreur lors du chargement de l'audio", error.response);
      alertError("Impossible de charger l'enregistrement audio");
    } finally {
      setIsLoadingAudio(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#031B4E] border-t-transparent"></div>
      </div>
    );
  }

  if (!call) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-12 text-center">
        <p className="text-slate-500 text-sm">Appel introuvable ou supprimé.</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#031B4E] text-white text-sm font-medium rounded-xl hover:bg-[#052975] transition-all"
        >
          <ArrowLeft size={16} /> Retour
        </button>
      </div>
    );
  }

  const isEntrant = call.direction === 'entrant';

  return (
    <div>
      <div className="px-6 py-5 bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-all"
              title="Retour"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <div className="flex items-center gap-2.5">
                <h1 className="text-xl font-bold text-[#031B4E] tracking-tight">Détails de l'appel</h1>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                  isEntrant ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-700'
                }`}>
                  {isEntrant ? <PhoneIncoming size={13} /> : <PhoneOutgoing size={13} />}
                  {isEntrant ? 'Entrant' : 'Sortant'}
                </span>
              </div>
            </div>
          </div>

          {call.client_phone && (
            <button
              onClick={() => {
                const formattedNumber = formatPhoneNumber(call.client_phone!);
                handleMakeCall(formattedNumber);
              }}
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-xl hover:bg-emerald-700 shadow-sm transition-all"
            >
              <Phone size={16} /> Rappeler
            </button>
          )}
        </div>
      </div>

      <div className="px-6 py-8 max-w-4xl mx-auto space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm space-y-1">
            <div className="flex items-center gap-2 text-slate-400 text-xs font-semibold uppercase tracking-wider">
              <User size={15} /> Client
            </div>
            <p className="text-base font-bold text-[#031B4E]">
              {call.client?.full_name || call.client_name || 'Client inconnu'}
            </p>
            <p className="text-sm text-slate-500 font-mono">
              {call.client_phone || call.client?.phone || 'Aucun numéro'}
            </p>
          </div>

          <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm space-y-1">
            <div className="flex items-center gap-2 text-slate-400 text-xs font-semibold uppercase tracking-wider">
              <Clock size={15} /> Durée
            </div>
            <p className="text-2xl font-bold text-[#031B4E] mt-1">
              {call.duration ? `${call.duration} secondes` : 'N/A'}
            </p>
          </div>

          <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm space-y-1">
            <div className="flex items-center gap-2 text-slate-400 text-xs font-semibold uppercase tracking-wider">
              <Calendar size={15} /> Date et Heure
            </div>
            <p className="text-sm font-semibold text-[#031B4E] mt-1">
              {call.created_at ? new Date(call.created_at).toLocaleString('fr-FR') : 'N/A'}
            </p>
          </div>
        </div>

        {call.recording_url && (
          <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm space-y-4">
            <div className="flex items-center gap-2 text-slate-700 text-sm font-bold border-b border-slate-100 pb-3">
              <Volume2 size={18} className="text-blue-600" /> Enregistrement de l'appel
            </div>
            <div className="pt-2">
              {!showPlayer ? (
                <button
                  onClick={handleLoadAudio}
                  disabled={isLoadingAudio}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm font-medium rounded-xl transition border border-blue-200"
                >
                  {isLoadingAudio ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Chargement de l'audio...</span>
                    </>
                  ) : (
                    <span>▶ Écouter l'enregistrement</span>
                  )}
                </button>
              ) : (
                audioSrc && (
                  <div className="w-full">
                    <AudioPlayer src={audioSrc} autoPlay={true} />
                  </div>
                )
              )}
            </div>
          </div>
        )}

        <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-2 text-slate-700 text-sm font-bold border-b border-slate-100 pb-3">
            <FileText size={18} className="text-blue-600" /> Notes et commentaires
          </div>
          <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap bg-slate-50/50 p-4 rounded-xl border border-slate-100">
            {call.notes || 'Aucune note enregistrée pour cet appel.'}
          </div>
        </div>

        <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2 text-slate-700 text-sm font-bold">
              <History size={18} className="text-blue-600" /> Historique des appels avec ce numéro
            </div>
            <span className="text-xs bg-slate-100 text-slate-600 px-2.5 py-0.5 rounded-full font-medium">
              {clientCalls.length} autre(s) appel(s)
            </span>
          </div>

          {loadingClientCalls ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : clientCalls.length === 0 ? (
            <p className="text-xs text-slate-400 italic py-2">Aucun autre appel enregistré pour ce numéro.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {clientCalls.map((pastCall) => {
                const isPastEntrant = pastCall.direction === 'entrant';
                return (
                  <div key={pastCall.id} className="py-3 flex items-center justify-between text-sm">
                    <div className="flex items-center gap-3">
                      <span className={`p-1.5 rounded-lg ${isPastEntrant ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-600'}`}>
                        {isPastEntrant ? <PhoneIncoming size={14} /> : <PhoneOutgoing size={14} />}
                      </span>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-800 capitalize">
                            {isPastEntrant ? 'Appel entrant' : 'Appel sortant'}
                          </span>
                          <span className="text-xs text-slate-400 font-mono">
                            ({formatDuration(pastCall.duration || 0)})
                          </span>
                        </div>
                        <p className="text-xs text-slate-400">
                          {pastCall.created_at ? new Date(pastCall.created_at).toLocaleString('fr-FR') : 'N/A'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate(`/calls/${pastCall.id}`)}
                      className="text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      Voir détails →
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {call.tags && call.tags.length > 0 && (
          <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm space-y-4">
            <div className="flex items-center gap-2 text-slate-700 text-sm font-bold border-b border-slate-100 pb-3">
              <Tag size={18} className="text-blue-600" /> Tags associés
            </div>
            <div className="flex flex-wrap gap-2">
              {call.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}